// Auth context — wraps the ported ClearSight/Logto auth-service.
//
// Endpoints (served by @iosentra/auth-service):
//   POST /api/v1/auth/login   { email, password } -> { accessToken, user }
//   GET  /api/v1/auth/me      -> current user (cookie or bearer)
//   POST /api/v1/auth/logout
//
// Roles carried in the JWT: super_admin, tenant_admin, project_manager,
// operator, viewer (+ authorized_projects / authorized_cameras scoping).

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { api, setAccessToken, setTenantId, setOnSessionExpired } from '@/lib/api';
import { useToast } from '@/components/ui/Toast';
import { useUIStore } from '@/stores/ui.store';

/** Licensed product plans the tenant has purchased — gates the sidebar. */
export type Plan = 'health-monitoring' | 'service-desk';

/** A customer (tenant) the user is a member of, with the role they hold in it. */
export interface Membership {
  tenantId: string;
  name: string;
  roles: string[];
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
  isSuperAdmin: boolean;
  tenantId: string;
  language?: string;
  /** Entitlements. Defaults to both when the backend omits it (dev). */
  plans?: Plan[];
  // ── Multi-customer (Logto Organizations) ──────────────────────────────
  /** The customer currently in context. */
  activeTenantId?: string;
  /** Every customer the user may switch to. */
  allowedTenantIds?: string[];
  /** Per-customer membership detail (drives the customer switcher). */
  memberships?: Membership[];
}

/** Roles that may see/use the Administration surface (Settings/Team/Automation). */
export const ADMIN_ROLES = new Set(['super_admin', 'tenant_admin', 'project_manager']);

/**
 * True when the user holds an admin role in the ACTIVE customer (or is a
 * super-admin). Single source of truth for both the nav visibility (AppLayout)
 * and the route guard (RequireAdmin) so they can't drift. Roles come from the
 * active membership when present, else the top-level role.
 */
export function isAdminUser(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  const activeRoles = user.memberships?.find((m) => m.tenantId === user.activeTenantId)?.roles
    ?? (user.role ? [user.role] : []);
  return !!user.isSuperAdmin || activeRoles.some((r) => ADMIN_ROLES.has(r));
}

/** Verification state surfaced during a Stage-4 challenge. */
export interface VerifyStatus {
  emailVerified: boolean;
  phoneVerified: boolean;
  hasPhone: boolean;
  phoneMasked: string | null;
  email?: string | null;
  smsAvailable: boolean;
}
/** Raw login-completion payload (session | MFA step | verification step). */
interface LoginResponse {
  mfaRequired?: boolean; loginId?: string; mfaMethods?: string[]; enrollMethods?: string[];
  verificationRequired?: boolean; verifyToken?: string; status?: VerifyStatus;
  accessToken?: string; user?: Record<string, unknown>;
}
/** Interpreted login outcome the UI branches on. */
export interface LoginOutcome {
  mfaRequired: boolean; loginId?: string; mfaMethods?: string[];
  // Factors the user has NO binding for yet and must SET UP (vs mfaMethods, which
  // are already-bound factors to verify). When present with an empty mfaMethods,
  // the login screen shows the authenticator-enrollment sub-step.
  enrollMethods?: string[];
  verificationRequired?: boolean; verifyToken?: string; verifyStatus?: VerifyStatus;
}
/** TOTP enrollment secret + server-rendered QR (data-URL) for the setup sub-step. */
export interface TotpEnrollment { secret: string; qrCode: string; }
/** Result of submitting a verification OTP. */
export interface VerifySubmitResult {
  complete: boolean;
  verified?: { email: boolean; phone: boolean };
  hasPhone?: boolean;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ requiresTotp?: boolean; challenge?: string }>;
  verifyTotp: (challenge: string, code: string) => Promise<void>;
  // Logto custom-UI login (Phase B): password → optional MFA (email/phone OTP or
  // TOTP) via the backend Experience-API proxy. Returns the next-step outcome:
  // full session, MFA step, or contact-verification step (Stage 4).
  logtoLogin: (email: string, password: string) => Promise<LoginOutcome>;
  /** Exchange a Logto access token (from the PKCE /callback) for an IOSentra session. */
  exchangeLogtoToken: (logtoAccessToken: string) => Promise<void>;
  logtoSendCode: (loginId: string, method: 'Email' | 'Phone') => Promise<void>;
  logtoVerify: (loginId: string, method: 'Email' | 'Phone' | 'Totp', code: string) => Promise<LoginOutcome>;
  // Login-time authenticator enrollment (only offered to factorless users): fetch
  // a fresh TOTP secret + QR, then confirm the first code to bind it and finish login.
  logtoEnrollTotp: (loginId: string) => Promise<TotpEnrollment>;
  logtoBindTotp: (loginId: string, code: string) => Promise<LoginOutcome>;
  // Forgot password (Logto-native, no Logto UI): request emails a branded OTP and
  // returns an opaque resetId; confirm verifies the code + sets the new password.
  requestPasswordReset: (email: string) => Promise<{ resetId?: string }>;
  confirmPasswordReset: (resetId: string, code: string, password: string) => Promise<void>;
  // Stage-4 contact verification (consumes the verifyToken from a login outcome).
  verifyAddPhone: (verifyToken: string, phone: string) => Promise<void>;
  verifySendCode: (verifyToken: string, channel: 'email' | 'phone') => Promise<void>;
  /** Submit an OTP; `complete` is true when verification finished and a session was issued. */
  verifySubmitCode: (verifyToken: string, channel: 'email' | 'phone', code: string) => Promise<VerifySubmitResult>;
  logout: () => Promise<void>;
  /** Switch the active customer (re-issues the token) and reload. */
  switchTenant: (tenantId: string) => Promise<void>;
  /** True if the tenant is entitled to a product plan (defaults open if unset). */
  hasPlan: (plan: Plan) => boolean;
}

/** Normalize a /login or /me payload (multi-customer shape) into AuthUser. */
function toAuthUser(raw: Record<string, unknown>): AuthUser {
  const roles = (raw.roles as string[] | undefined) ?? [];
  const role = (raw.role as string | undefined) ?? roles[0] ?? 'viewer';
  const activeTenantId = (raw.activeTenantId as string | undefined) ?? (raw.tenantId as string);
  return {
    id: raw.id as string,
    email: raw.email as string,
    name: (raw.name as string) ?? (raw.email as string),
    role,
    isSuperAdmin: Boolean(raw.isSuperAdmin) || role === 'super_admin',
    tenantId: activeTenantId,
    language: raw.language as string | undefined,
    plans: raw.plans as Plan[] | undefined,
    activeTenantId,
    allowedTenantIds: (raw.allowedTenantIds as string[] | undefined) ?? [],
    memberships: (raw.memberships as Membership[] | undefined) ?? [],
  };
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const { t } = useTranslation('components');
  const toast = useToast();

  // When a 401 can't be recovered by a refresh (session truly over), the api
  // client calls this: clear the session so App.tsx falls back to the login
  // screen, and tell the operator why (instead of a silent dead UI).
  useEffect(() => {
    setOnSessionExpired(() => {
      setAccessToken(null);
      setTenantId(null);
      setUser(null);
      queryClient.clear();
      toast.info(t('auth.sessionExpired'), { duration: 7000 });
    });
    return () => setOnSessionExpired(null);
  }, [queryClient, toast, t]);

  // Restore session on mount (cookie-based). `/auth/me` is the session PROBE:
  // a 401 here means "not signed in", so the api client deliberately does NOT
  // run its refresh-or-expire path for it (that would toast "session expired"
  // at every fresh visitor on the login page). We still want a returning user
  // with a live refresh cookie to be restored silently, so do that one retry
  // explicitly here — and stay quiet if it fails.
  useEffect(() => {
    let cancelled = false;
    const probe = () => api.get<Record<string, unknown>>('/api/v1/auth/me');
    probe()
      .catch(async () => {
        // Access token missing/expired — try one refresh, then re-probe.
        const res = await api.post('/api/v1/auth/refresh');
        const token = (res.data as { accessToken?: string })?.accessToken;
        if (token) setAccessToken(token);
        return probe();
      })
      .then((res) => {
        if (cancelled) return;
        const u = toAuthUser(res.data);
        setUser(u);
        setTenantId(u.activeTenantId ?? u.tenantId);
        // Apply the user's saved UI prefs (language → dir/i18n, theme, …) from
        // the backend so their language choice persists across sessions/devices,
        // not just within the browser that set it.
        void useUIStore.getState()._initFromApi();
      })
      .catch(() => { if (!cancelled) setUser(null); })  // not signed in — no toast
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const applySession = (data: { accessToken: string; user: Record<string, unknown> }) => {
    setAccessToken(data.accessToken);
    const u = toAuthUser(data.user);
    setTenantId(u.activeTenantId ?? u.tenantId);
    setUser(u);
    // Sync saved UI prefs (language → dir/i18n, theme, …) right after sign-in so
    // the chosen language takes effect immediately + persists across devices.
    void useUIStore.getState()._initFromApi();
  };

  // Unified-auth (AUTH_FLOW=oidc): exchange a validated Logto ACCESS TOKEN for an
  // IOSentra session at POST /auth/session. Called by the /callback page after
  // the @logto/react PKCE redirect completes. The Logto token is sent as a
  // one-shot Bearer; the response is the normal IOSentra session.
  const exchangeLogtoToken = async (logtoAccessToken: string) => {
    const res = await api.post<{ accessToken: string; user: Record<string, unknown> }>(
      '/api/v1/auth/session',
      undefined,
      { headers: { Authorization: `Bearer ${logtoAccessToken}` } },
    );
    applySession(res.data);
  };

  // Returns { requiresTotp, challenge } when the account has 2FA — the caller
  // then collects a code and calls verifyTotp(). Otherwise the session is set.
  const login = async (email: string, password: string): Promise<{ requiresTotp?: boolean; challenge?: string }> => {
    const res = await api.post<{ accessToken?: string; user?: Record<string, unknown>; requiresTotp?: boolean; challenge?: string }>(
      '/api/v1/auth/login',
      { email, password },
    );
    if (res.status === 202 && res.data.requiresTotp) return { requiresTotp: true, challenge: res.data.challenge };
    applySession(res.data as { accessToken: string; user: Record<string, unknown> });
    return {};
  };

  const verifyTotp = async (challenge: string, code: string) => {
    const res = await api.post<{ accessToken: string; user: Record<string, unknown> }>(
      '/api/v1/auth/login/verify-totp',
      { challenge, code },
    );
    applySession(res.data);
  };

  // ── Logto custom-UI login (Phase B) ──────────────────────────────────────
  // A login completion can resolve three ways: a full session, an MFA step
  // (mfaRequired), or a contact-verification step (verificationRequired, Stage 4).
  const interpretLogin = (d: LoginResponse): LoginOutcome => {
    if (d.verificationRequired && d.verifyToken) {
      return { mfaRequired: false, verificationRequired: true, verifyToken: d.verifyToken, verifyStatus: d.status };
    }
    if (d.mfaRequired) return { mfaRequired: true, loginId: d.loginId, mfaMethods: d.mfaMethods, enrollMethods: d.enrollMethods };
    applySession(d as { accessToken: string; user: Record<string, unknown> });
    return { mfaRequired: false };
  };
  const logtoLogin = async (email: string, password: string) => {
    const res = await api.post<LoginResponse>('/api/v1/auth/logto/start', { email, password });
    return interpretLogin(res.data);
  };
  const logtoSendCode = async (loginId: string, method: 'Email' | 'Phone') => {
    await api.post('/api/v1/auth/logto/mfa/send', { loginId, method });
  };
  const logtoVerify = async (loginId: string, method: 'Email' | 'Phone' | 'Totp', code: string) => {
    const res = await api.post<LoginResponse>('/api/v1/auth/logto/mfa/verify', { loginId, method, code });
    return interpretLogin(res.data);
  };
  const logtoEnrollTotp = async (loginId: string): Promise<TotpEnrollment> => {
    const res = await api.post<TotpEnrollment>('/api/v1/auth/logto/mfa/enroll', { loginId });
    return res.data;
  };
  const logtoBindTotp = async (loginId: string, code: string) => {
    const res = await api.post<LoginResponse>('/api/v1/auth/logto/mfa/enroll/confirm', { loginId, code });
    return interpretLogin(res.data);
  };
  const requestPasswordReset = async (email: string): Promise<{ resetId?: string }> => {
    const res = await api.post<{ ok: boolean; resetId?: string }>('/api/v1/auth/password/reset/request', { email });
    return { resetId: res.data.resetId };
  };
  const confirmPasswordReset = async (resetId: string, code: string, password: string): Promise<void> => {
    await api.post('/api/v1/auth/password/reset/confirm', { resetId, code, password });
  };

  // ── Contact verification (Stage 4) ────────────────────────────────────────
  const verifyAddPhone = async (verifyToken: string, phone: string) => {
    await api.post('/api/v1/auth/verify/phone', { verifyToken, phone });
  };
  const verifySendCode = async (verifyToken: string, channel: 'email' | 'phone') => {
    await api.post('/api/v1/auth/verify/send', { verifyToken, channel });
  };
  // Submit an OTP. On full completion a session is issued (complete:true); else
  // returns the per-channel verified flags so the caller can advance the flow.
  const verifySubmitCode = async (
    verifyToken: string, channel: 'email' | 'phone', code: string,
  ): Promise<VerifySubmitResult> => {
    const res = await api.post<{ accessToken?: string; user?: Record<string, unknown>; verified?: { email: boolean; phone: boolean }; hasPhone?: boolean }>(
      '/api/v1/auth/verify/code', { verifyToken, channel, code },
    );
    if (res.data.accessToken && res.data.user) {
      applySession(res.data as { accessToken: string; user: Record<string, unknown> });
      return { complete: true };
    }
    return { complete: false, verified: res.data.verified, hasPhone: res.data.hasPhone };
  };

  const switchTenant = async (tenantId: string) => {
    const res = await api.post<{ accessToken: string; user: Record<string, unknown> }>(
      '/api/v1/auth/switch-tenant',
      { tenantId },
    );
    // Adopt the new customer's token IN MEMORY, so subsequent requests are
    // scoped to it immediately — no page reload. Reloading here used to throw
    // away the in-memory token and race the just-written cookie, which left the
    // app on the OLD customer until a full logout/login.
    setAccessToken(res.data.accessToken);
    const u = toAuthUser(res.data.user);
    setTenantId(u.activeTenantId ?? u.tenantId);
    setUser(u);
    // Sync saved UI prefs (language → dir/i18n, theme, …) for the new customer.
    void useUIStore.getState()._initFromApi();
    // Drop ALL cached tenant-scoped queries so every widget refetches under the
    // new active customer (replaces the old window.location.reload()).
    queryClient.clear();
  };

  const logout = async () => {
    try {
      await api.post('/api/v1/auth/logout');
    } finally {
      setAccessToken(null);
      setTenantId(null);
      setUser(null);
    }
  };

  // Entitlement check. When the backend doesn't send `plans` (dev/demo), grant
  // everything so the full app is reachable.
  const hasPlan = (plan: Plan): boolean => {
    if (!user) return false;
    if (!user.plans || user.plans.length === 0) return true;
    return user.plans.includes(plan);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, verifyTotp, logtoLogin, exchangeLogtoToken, logtoSendCode, logtoVerify, logtoEnrollTotp, logtoBindTotp, requestPasswordReset, confirmPasswordReset, verifyAddPhone, verifySendCode, verifySubmitCode, logout, switchTenant, hasPlan }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
